import Style from "ol/style/Style";
import Icon from "ol/style/Icon";
import RegularShape from "ol/style/RegularShape";
import Circle from "ol/style/Circle";
import Text from "ol/style/Text";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Feature from "ol/Feature";

import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import MultiPoint from "ol/geom/MultiPoint";
import LineString from "ol/geom/LineString";
import MultiLineString from "ol/geom/MultiLineString";
import Polygon from "ol/geom/Polygon";
import MultiPolygon from "ol/geom/MultiPolygon";

import { isArray } from "../../common/utils";

export const Default = new Style();
export const Hidden = new Style({});

export const PinRed = new Style({
  image: new Icon({
    src: '/static/pins/pin.red.png',
    //snapToPixel: false,
    anchor: [0.5, 1.0],
    opacity: 1.0
  })
});

export const PinWithShadow = [
  new Style({
    image: new Icon({
      src: '/static/pins/pins.bg.png',
      scale: 0.5,
      //snapToPixel: false,
      anchor: [0.0, 1.0],
      opacity: 0.9
    })
  }),
  new Style({
    image: new Icon({
      src: '/static/pins/pins.png',
      scale: 0.6,
      //snapToPixel: false,
      anchor: [0.5, 1.0],
      opacity: 0.8
    })
  })
];

export const FlagBlue = new Style({
  image: new Icon({
    src: '/static/pins/flag.blue.png',
    scale: 0.5,
    //snapToPixel: false,
    //anchorXUnits: 'fraction',
    //anchorYUnits: 'fraction',
    anchor: [0.1, 0.95],
    size: [128, 128]
  })
});

export function ImageCircle(style) {
  let c:any = {
    radius: style.radius || 6
  };
  if (style.fill) {
    c.fill = new Fill(style.fill);
  }
  if (style.stroke) {
    c.stroke = new Stroke(style.stroke);
  }
  return new Style({
    image: new Circle(c)
  });
};

export function OsmPoi(feature, resolution) {
  return {
    points:[{
      icon: {
        src: '/static/images/icons/aed.png', // + feature.get('icon'),
        size: [32,37],
        offset: [8,9],
        scale: resolution>=2000 ? 0.5 : resolution>=1000 ? 0.7 : resolution>=500 ? 0.85 : 1.0,
        rotateWithView: true
      }
    }]
  }
}

/**
 * Display feature label with rotation
 * @param feature 
 * @param resolution 
 */
export function Label(feature:Feature, resolution?:number) {
  //let geom = feature.getGeometry();
  let label = '', rotation = 0;
  if (feature) {
    label = (feature.get('label') || feature.get('name') || feature.get('text') || '') + '';
    rotation = parseFloat('0' + feature.get('rotation'));
  }
  return new Style({
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.0)'
    }),
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0)',
      width: 0
    }),
    image: new Circle({
      radius: 0,
      stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0.0)',
        width: 0
      }),
      fill: new Fill({
        color: 'rgba(255, 255, 255, 0.0)'
      })
    }),
    text: new Text({
      rotation: rotation,
      text: label,
      overflow: true,
      font: '12px sans-serif',
      fill: new Fill({color: '#000000'}),
      stroke: new Stroke({
        color: 'rgba(255,255,0,0.9)',
        width: 2
      })
    })
  });
}

// drawing and measuring style
function DynamicStyle(feature:Feature, resolution?:number, color?:string) {
  if (!feature) {
    return {};
  }
  let white = 'rgba(255, 255, 255, 0.9)';
  let blue = color || 'rgba(0, 153, 255, 0.95)';
  let width = 3;
  let labelStyle = feature.get('labelStyle');
  let geom = feature.getGeometry();
  let style;
  // LABEL_ONLY is set in Measurer.ts
  if (feature.get('LABEL_ONLY') && labelStyle) {
    labelStyle = Object.assign({
      font: '13px sans-serif',
      fill: { color: blue },
      stroke: {
        color: white,
        width: width
      },
      rotateWithView: true,
      zIndex: Infinity
    }, labelStyle);
    style = {
      label: labelStyle,
      fills: [{
        color: 'rgba(255, 255, 255, 0.0)'
      }],
      strokes: [{
        color: 'rgba(255, 255, 255, 0.0)',
        width: 0
      }],
      points: [{
        circle: {
          radius: 0,
          fill: {
            color: 'rgba(255, 255, 255, 0.0)'
          },
          stroke: {
            color: 'rgba(255, 255, 255, 0.0)',
            width: 0
          }
        }
      }]
    };
  } else {
    let lineDash = color ? undefined : [6, 6];
    if (geom instanceof Point) {
      style = {
        points: [{
          circle: {
            radius: width + 2,
            fill: { color: white },
            stroke:{
              color: blue,
              width: width / 1.6
            }
          },
          zIndex: Infinity
        }]
      };
    } else if (geom instanceof LineString) {
      style = {
        strokes: [{
            color: white,
            width: width + 2
          },{
            color: blue,
            width: width,
            lineDash: lineDash
          }]
      };
    } else if (geom instanceof Polygon) {
      let label = (feature.get('label') || feature.get('name') || feature.get('text') || '') + '';
      labelStyle = Object.assign({
        font: '13px sans-serif',
        fill: {color: blue},
        stroke: {
          color: white,
          width: width
        },
        zIndex: Infinity
      }, labelStyle);
      style = {
        fills: [{ color: 'rgba(255, 255, 255, 0.2)'}],
        strokes: [{
          color: white,
          width: width + 2
        },{
          color: blue,
          width: width,
          lineDash: lineDash
        }],
        label: labelStyle
      }
    }
  }
  return style;
}

export function Aoi(feature:Feature, resolution?:number) {
  if (!feature) {
    return {};
  }
  let basemapType = feature['NATIVE_SOURCE'].NATIVE_LAYERS[0].SOFTOO_MAPS[0].map.BaseMapType;
  let strokeColorBack = basemapType=='map' ? 'rgba(0,0,255,0.3)' : 'rgba(255,255,255,0.5)';
  let strokeColor = 'rgba(251,96,127,1.0)';
  let style:{[key:string] : any} = {
    strokes: [{
      color: strokeColorBack,
      width: 4
    },{
      color: strokeColor,
      width: 1
    }]
  };
  let strokes:{[key:string]:any}[] = (feature.get('SOFTOO_STYLE') || {}).strokes;
  if (strokes) {
    if (strokes.length==1) {
      strokes[0].color = strokes[0].color || strokeColor;
      strokes[0].width = strokes[0].width || 1;
    } else if (strokes.length>1) {
      strokes[0].color = strokes[0].color || strokeColorBack;
      strokes[0].width = strokes[0].width || 4;
      strokes[1].color = strokes[1].color || strokeColor;
      strokes[1].width = strokes[1].width || 1;
    }
    style = {
      strokes: strokes
    };
  }
  return style;

  /*let style = Object.assign({
    strokeBackWidth: 4,
    strokeForeWidth: 1,
  },feature.get('SOFTOO_STYLE') || {});
  return feature.get('SOFTOO_STYLE') || {
    strokes: [{
      color: strokeColorBack,
      width: style.strokeBackWidth
    },{
      color: strokeColor,
      width: style.strokeForeWidth
    }]
  };*/
}

export function Draw(feature:Feature, resolution?:number) {
  let style = DynamicStyle(feature, resolution,'rgba(255,105,97,0.95)');
  return style;
};

export function Measure(feature:Feature, resolution?:number) {
  let style = DynamicStyle(feature, resolution);
  return create(feature, resolution, style);
}

export function Highlight(feature:Feature, resolution?:number) {
  let layer = feature['NATIVE_SOURCE'].NATIVE_LAYERS[0];
  let fillColor = 'rgba(0,255,255,0.12)';
  let strokeColor = 'rgba(0,255,255,0.6)';
  let circleColor = 'rgba(0,255,255,0.9)';
  let featureStyles = (feature.getStyle() || layer.getStyle() ? layer.getStyle()(feature,resolution) : null) || [];
  let radius=5, width=2;

  if (featureStyles && !isArray(featureStyles)) {
    featureStyles = [featureStyles];
  }
  let icon;
  featureStyles.forEach((style) => {
    let s = style.getImage ? style.getImage() : null;
    if (s && s instanceof Circle) {
      radius = Math.max(radius,s.getRadius());
    } else if (s && s instanceof (Icon)) {
      let size = s.getSize();
      let anchor = s.getAnchor();
      anchor = [anchor[0]/size[0],anchor[1]/size[1]];
      if (s.getAnchor() != [0.5,0.5]) {
        icon = {
          anchor: anchor,
          src: s.getSrc(),
          size: size,
          scale: s.getScale() * 1.25,
          rotateWithView: s.getRotateWithView()
        }
      } else {
        radius = s.getSize()[0] * s.getScale() / 2;
      }
    }
    s = style.getStroke ? style.getStroke() : null;
    if (s) {
      width = Math.max(width, s.getWidth());
    }
  });
  radius += 1;
  width += 2;
  //let label = feature.get('SOFTOO_LABEL');
  /*if (label) {
    label.font = '14px sans-serif';
  }*/
  let points = icon ? {
    icon: icon
  } : {
    circle: {
      radius: radius,
      //fill: { color: fillColor },
      stroke: {
        color: circleColor,
        width: 2
      }
    }
  };
  let hightlight = create(feature, resolution, {
    points: [points],
    strokes: [{
      color: strokeColor,
      width: width
    }],
    fills: [{
      color: fillColor
    }]
    //,
    //label: label
  });
  // add highlight on top of original feature style
  return featureStyles.concat(hightlight);
}

export function Search(feature:Feature, resolution?:number) {
  let strokeColor = 'rgba(255,0,0,0.9)';
  let fillColor = 'rgba(0,0,255,0.1)';
  return {
    points:[{
      icon: {
        src: '/static/images/icons/' + (feature ? feature.get('icon') : 'abc.png') ,
        size: [32,37],
        anchor: [0.5,1.0],
        scale: resolution>=240 ? 0.25 : resolution>=120 ? 0.4 : resolution>=60 ? 0.6 : resolution>=30 ? 0.8 : 1.0,
        rotateWithView: true
      }
    }],
    Xpoints: [{
      circle: {
        radius: 6,
        fill: { color: fillColor },
        stroke: {
          color: strokeColor,
          width: 2
        }
      }
    }],
    strokes: [{
      color: strokeColor,
      width: 3
    }]
  };
}

export function AoI(feature:Feature, resolution?:number) {
  if (!feature) {
    return {};
  }
  let basemapType = feature['NATIVE_SOURCE'].NATIVE_LAYERS[0].SOFTOO_MAPS[0].map.BaseMapType;
  let strokeColorBack = basemapType=='map' ? 'rgba(0,0,255,0.3)' : 'rgba(255,255,255,0.5)';
  let strokeColor = 'rgba(251,96,127,1.0)';
  return {
    strokes: [{
      color: strokeColorBack,
      width: 4
    },{
      color: strokeColor,
      width: 1
    }]
  };
}

export function Select(feature:Feature, resolution?:number) {
  let fillColor = 'rgba(0,0,255,0.1)';
  let strokeColor = 'rgba(0,0,255,0.5)';
  let s:any = {
    points: [{
      circle: {
        radius: 6,
        fill: { color: fillColor },
        stroke: {
          color: strokeColor,
          width: 2
        }
      }
    }],
    strokes: [{
      color: strokeColor,
      width: 4
    }],
    fills: [{
      color: fillColor
    }]
  };
  if (feature) {
    let label = feature.get('SOFTOO_LABEL');
    if (label) {
      label.fill = { color: strokeColor };
      s.label = label;
    }
  }
  
  return s;
}

/**
 * @param resolution 
 * @param style: {label:{},points[{icon:{}},{circles:{}},{shapes:{}}],strokes:[{}],fills:[{}]}
 */
export function create(feature:Feature, resolution?:number, style?:{[key:string] : any}) {
  if (!(feature && style)) {
    return null;
  }
  let geom = feature.getGeometry();
  let _style:{[key:string] : any} = {};
  if (style.zIndex) {
    _style.zIndex = style.zIndex;
  }
  if (style.label) {
    //defaults
    let fontSize = '12px';
    if (feature['NATIVE_SOURCE'] && !(feature.getGeometry() instanceof Point)) {
      let zoom = feature['NATIVE_SOURCE'].NATIVE_LAYERS[0].SOFTOO_MAPS[0].getZoom();
      if (zoom>=20) {
        fontSize = Math.round(zoom * 1.5) + 'px';
      } else if (zoom>16) {
        fontSize = Math.round(zoom * 0.9) + 'px';
      } else if (zoom>15) {
        fontSize = Math.round(zoom * 0.8) + 'px';
      } else {
        fontSize = Math.round(zoom * 0.6) + 'px';
      }
    }
    let label:{[key:string] : any} = {
      text: (feature.get('label') || feature.get('name') || feature.get('text') || '') + '',
      rotation: parseFloat('0' + feature.get('rotation')),
      font: fontSize + ' AngsanaNew',
      rotateWithView: true,
      zIndex: Infinity,
      overflow: true
    };
    label = Object.assign(label, feature.get('labelStyle') || {}, style.label);
    if (style.label.fill) {
      label.fill = new Fill(style.label.fill);
    }
    if (style.label.stroke) {
      label.stroke = new Stroke(style.label.stroke);
    }
    _style.text = new Text(label);
  }
  let n:number;
  let isPoint = geom instanceof Point || geom instanceof MultiPoint;
  let isLine = geom instanceof LineString || geom instanceof MultiLineString;
  let isPolygon = geom instanceof Polygon || geom instanceof MultiPolygon;
  if (isPoint && style.points) {
    n = style.points.length - 1;
    if (n<0) {
      return new Style(_style);
    }
    return style.points.map((p:{[key:string] : any}, i:number) => {
      let s:any = {};
      if (p.icon) {
        s.image = new Icon(p.icon);
      } else if (p.circle || p.shape) {
        s = Object.assign({}, p.circle || p.shape || {});
        if (s.fill) {
          s.fill = new Fill(s.fill);
        }
        if (s.stroke) {
          s.stroke = new Stroke(s.stroke);
        }
        if (p.circle) {
          s.image = new Circle(s);
        } else {
          s.image = new RegularShape(s);
        }
      }
      if (i==n) {
        s = Object.assign(_style,s);
      }
      return new Style(s);
    });
  } else if (isLine && style.strokes) {
    n = style.strokes.length - 1;
    if (n<0) {
      return new Style(_style);
    }
    return style.strokes.map((s:any, i:number) => {
      let _s = {
        stroke: new Stroke(s)
      }
      if (i==n) {
        _s = Object.assign(_style, _s);
      }
      return new Style(_s);
    });
  } else if (isPolygon) {
    // openlayer 5 issue: if polygon is not filled,
    // ol.interaction.Select does not select it untill
    // mouseover or click its boundary
    if (!style.fills) {
      style.fills = [{
        color: 'rgba(255,255,255,0)'
      }];
    }
    n = style.fills ? style.fills.length : 0;
    n += (style.strokes ? style.strokes.length : 0);
    n--;
    if (n<0) {
      return new Style(_style);
    }
    let i=0, fs=[], bs=[];
    if (style.fills) {
      fs = style.fills.map((f:any) => {
        let s:any = {
          fill: new Fill(f)
        };
        if (i==n) {
          s = Object.assign(s,_style);
        }
        i++;
        return new Style(s);
      });
    }
    if (style.strokes) {
      bs = style.strokes.map((s:any) => {
        let _s = {
          stroke: new Stroke(s)
        }
        if (i==n) {
          _s = Object.assign(_s, _style);
        }
        i++;
        return new Style(_s);
      });
      return fs.concat(bs);
    }
  }
  return new Style(_style);
}
